import { FormEvent, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal } from "../Modal";
import {
  studentService,
  type Class,
  type Student,
} from "../../services/studentService";
import { STUDENT_STATUS_LABELS } from "./studentDisplay";
import { Field, FormSection } from "../FormLayout";

type Props = {
  student: Student;
  classes: Class[];
  /** Called with the saved record after a save, or with nothing on cancel. */
  onClose: (saved?: Student) => void;
};

const FORM_ID = "student-edit-form";
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const EDITABLE_STATUSES: Student["status"][] = [
  "active",
  "inactive",
  "suspended",
  "transferred",
  "graduated",
];
// These have their own workflow, which keeps certificates and history.
const LIFECYCLE_STATUSES = ["transferred", "graduated", "withdrawn", "expelled"];

const FORM_FIELDS = [
  "first_name", "last_name", "phone_number", "date_of_birth", "gender",
  "nationality", "blood_group", "current_class", "status", "residence",
  "admission_date", "previous_school", "surepay_number", "transport_route",
  "pickup_location", "medical_conditions", "allergies", "medications",
  "doctor_name", "doctor_phone", "notes",
];

type FieldErrors = Record<string, string>;

/** Server validation messages keyed by field, plus one for the whole form. */
export function studentEditErrors(error: unknown): FieldErrors {
  const data = (error as { response?: { data?: unknown } } | null)?.response
    ?.data;
  if (!data || typeof data !== "object")
    return error ? { form: "The changes could not be saved. Please try again." } : {};
  return Object.fromEntries(
    Object.entries(data as Record<string, unknown>).map(([field, message]) => [
      ["detail", "non_field_errors"].includes(field) ? "form" : field,
      Array.isArray(message) ? message.join(" ") : String(message),
    ]),
  );
}

export default function StudentEditModal({
  student,
  classes,
  onClose,
}: Props) {
  const queryClient = useQueryClient();
  const [usesTransport, setUsesTransport] = useState(student.uses_transport);
  const [status, setStatus] = useState(student.status);
  const update = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      studentService.updateStudent(student.id, data),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      onClose(saved);
    },
  });
  const errors = studentEditErrors(update.error);
  // Errors for fields this form has no input for are listed at the top.
  const unknownErrors = Object.entries(errors).filter(
    ([field]) => field !== "form" && !FORM_FIELDS.includes(field),
  );

  const statuses = EDITABLE_STATUSES.includes(student.status)
    ? EDITABLE_STATUSES
    : [...EDITABLE_STATUSES, student.status];
  const bloodGroups =
    student.blood_group && !BLOOD_GROUPS.includes(student.blood_group)
      ? [...BLOOD_GROUPS, student.blood_group]
      : BLOOD_GROUPS;
  const today = new Date().toISOString().slice(0, 10);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    const data: Record<string, unknown> = {
      first_name: text("first_name"),
      last_name: text("last_name"),
      phone_number: text("phone_number"),
      date_of_birth: text("date_of_birth"),
      gender: text("gender"),
      nationality: text("nationality"),
      blood_group: text("blood_group"),
      current_class: Number(form.get("current_class")),
      status: text("status"),
      residence: text("residence"),
      admission_date: text("admission_date"),
      previous_school: text("previous_school"),
      surepay_number: text("surepay_number"),
      uses_transport: usesTransport,
      medical_conditions: text("medical_conditions"),
      allergies: text("allergies"),
      medications: text("medications"),
      doctor_name: text("doctor_name"),
      doctor_phone: text("doctor_phone"),
      notes: text("notes"),
    };
    if (usesTransport) {
      data.transport_route = text("transport_route");
      data.pickup_location = text("pickup_location");
    }
    update.mutate(data);
  };

  return (
    <Modal
      isOpen
      size="3xl"
      title={`Edit ${student.full_name}`}
      onClose={() => {
        if (!update.isPending) onClose();
      }}
      footer={
        <>
          <button
            type="button"
            onClick={() => onClose()}
            disabled={update.isPending}
            className="btn btn-outline"
          >
            Cancel
          </button>
          <button
            type="submit"
            form={FORM_ID}
            disabled={update.isPending}
            className="btn btn-primary"
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={submit} className="space-y-5">
        <p className="text-xs text-gray-500">
          <span className="font-mono">{student.student_id}</span>
          {student.admission_number && ` · Adm. ${student.admission_number}`} ·
          Login <span className="font-mono">{student.user.username}</span> does
          not change when the name is corrected.
        </p>

        {(errors.form || unknownErrors.length > 0) && (
          <div role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">
            {[errors.form, ...unknownErrors.map(([field, message]) => `${field.replaceAll("_", " ")}: ${message}`)]
              .filter(Boolean)
              .join(" ")}
          </div>
        )}

        <FormSection title="Learner details">
          <Field label="First name *" name="first_name" errors={errors}>
            <input
              required
              minLength={2}
              name="first_name"
              defaultValue={student.user.first_name}
              className="input mt-1"
              autoFocus
            />
          </Field>
          <Field label="Last name *" name="last_name" errors={errors}>
            <input
              required
              minLength={2}
              name="last_name"
              defaultValue={student.user.last_name}
              className="input mt-1"
            />
          </Field>
          <Field label="Date of birth *" name="date_of_birth" errors={errors}>
            <input
              required
              type="date"
              max={today}
              name="date_of_birth"
              defaultValue={student.date_of_birth}
              className="input mt-1"
            />
          </Field>
          <Field label="Gender *" name="gender" errors={errors}>
            <select
              required
              name="gender"
              defaultValue={student.gender}
              className="input mt-1"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </Field>
          <Field label="Nationality *" name="nationality" errors={errors}>
            <input
              required
              minLength={2}
              name="nationality"
              defaultValue={student.nationality}
              className="input mt-1"
            />
          </Field>
          <Field label="Blood group" name="blood_group" errors={errors}>
            <select
              name="blood_group"
              defaultValue={student.blood_group || ""}
              className="input mt-1"
            >
              <option value="">Not known</option>
              {bloodGroups.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Learner phone"
            name="phone_number"
            errors={errors}
            hint="Optional. Parents' numbers are kept on their own records."
          >
            <input
              type="tel"
              maxLength={15}
              name="phone_number"
              defaultValue={student.user.phone_number || ""}
              className="input mt-1"
              placeholder="e.g. 0700 000000"
            />
          </Field>
        </FormSection>

        <FormSection title="Placement & enrolment">
          <Field label="Class *" name="current_class" errors={errors}>
            <select
              required
              name="current_class"
              defaultValue={student.current_class || ""}
              className="input mt-1"
            >
              <option value="" disabled>
                Select class
              </option>
              {classes.map((item) => {
                const isCurrent = item.id === student.current_class;
                const full = !isCurrent && item.available_spots <= 0;
                return (
                  <option key={item.id} value={item.id} disabled={full}>
                    {item.name}
                    {isCurrent
                      ? " · current class"
                      : full
                        ? " · full"
                        : ` · ${item.available_spots} places left`}
                  </option>
                );
              })}
            </select>
          </Field>
          <Field
            label="Status *"
            name="status"
            errors={errors}
            hint={
              LIFECYCLE_STATUSES.includes(status) && status !== student.status
                ? "Transfers and graduations are best recorded under Student lifecycle, which keeps the certificate and history."
                : undefined
            }
          >
            <select
              name="status"
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as Student["status"])
              }
              className="input mt-1"
            >
              {statuses.map((value) => (
                <option key={value} value={value}>
                  {STUDENT_STATUS_LABELS[value] || value}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Day or boarding *"
            name="residence"
            errors={errors}
            hint="Decides which fees the learner is billed."
          >
            <select
              name="residence"
              defaultValue={student.residence || "day"}
              className="input mt-1"
            >
              <option value="day">Day scholar</option>
              <option value="boarding">Boarder</option>
            </select>
          </Field>
          <Field label="Admission date *" name="admission_date" errors={errors}>
            <input
              required
              type="date"
              max={today}
              name="admission_date"
              defaultValue={student.admission_date}
              className="input mt-1"
            />
          </Field>
          <Field label="Previous school" name="previous_school" errors={errors}>
            <input
              name="previous_school"
              maxLength={200}
              defaultValue={student.previous_school || ""}
              className="input mt-1"
            />
          </Field>
          <Field
            label="SurePay number"
            name="surepay_number"
            errors={errors}
            hint="Fees paid through SurePay are matched to this learner by this number."
          >
            <input
              name="surepay_number"
              maxLength={30}
              defaultValue={student.surepay_number || ""}
              className="input mt-1 font-mono"
              placeholder="e.g. SP-4417"
            />
          </Field>
        </FormSection>

        <FormSection title="Transport">
          <label className="flex items-center gap-3 text-sm font-medium text-gray-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={usesTransport}
              onChange={(event) => setUsesTransport(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            Uses school transport
          </label>
          {usesTransport && (
            <>
              <Field label="Route" name="transport_route" errors={errors}>
                <input
                  name="transport_route"
                  maxLength={100}
                  defaultValue={student.transport_route || ""}
                  className="input mt-1"
                />
              </Field>
              <Field label="Pickup point" name="pickup_location" errors={errors}>
                <input
                  name="pickup_location"
                  maxLength={200}
                  defaultValue={student.pickup_location || ""}
                  className="input mt-1"
                />
              </Field>
            </>
          )}
        </FormSection>

        <FormSection
          title="Health"
          description="Allergies and conditions are shown as an alert on the learner's record."
        >
          <Field label="Medical conditions" name="medical_conditions" errors={errors}>
            <textarea
              rows={2}
              name="medical_conditions"
              defaultValue={student.medical_conditions || ""}
              className="input mt-1 h-auto"
            />
          </Field>
          <Field label="Allergies" name="allergies" errors={errors}>
            <textarea
              rows={2}
              name="allergies"
              defaultValue={student.allergies || ""}
              className="input mt-1 h-auto"
            />
          </Field>
          <Field label="Medications" name="medications" errors={errors} className="sm:col-span-2">
            <textarea
              rows={2}
              name="medications"
              defaultValue={student.medications || ""}
              className="input mt-1 h-auto"
            />
          </Field>
          <Field label="Doctor's name" name="doctor_name" errors={errors}>
            <input
              name="doctor_name"
              maxLength={100}
              defaultValue={student.doctor_name || ""}
              className="input mt-1"
            />
          </Field>
          <Field label="Doctor's phone" name="doctor_phone" errors={errors}>
            <input
              type="tel"
              name="doctor_phone"
              maxLength={15}
              defaultValue={student.doctor_phone || ""}
              className="input mt-1"
            />
          </Field>
        </FormSection>

        <FormSection title="Notes">
          <Field label="Notes" name="notes" errors={errors} className="sm:col-span-2">
            <textarea
              rows={3}
              name="notes"
              defaultValue={student.notes || ""}
              className="input mt-1 h-auto"
            />
          </Field>
        </FormSection>
      </form>
    </Modal>
  );
}
